import React from 'react';
import { lbl, inp, row2 } from './shared';

function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const CONTACT_METHODS = ['Phone', 'Email', 'In Person', 'Walk-in', 'Mail', 'Internet / Online'];

const PRODUCT_LABELS = {
  partD:      'Stand-alone Medicare Prescription Drug Plans (Part D)',
  partC:      'Medicare Advantage Plans (Part C) and Cost Plans',
  dental:     'Dental / Vision / Hearing Products',
  hospital:   'Hospital Indemnity Products',
  supplement: 'Medicare Supplement (Medigap) Products',
};

export default function SoaAgentSection({ data, onChange }) {
  const today = new Date().toLocaleDateString('en-US');
  const selectedProducts = (data.soaProducts || [])
    .map(id => ({ id, label: PRODUCT_LABELS[id] }))
    .filter(product => product.label);
  const selectedProductLabels = selectedProducts.map(product => product.label);
  const representedPlans = (data.soaPlansRepresented || '')
    .split(',')
    .map(plan => plan.trim())
    .filter(Boolean);
  const fullName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ');
  const fullAddress = [data.address, data.city, data.state, data.zip].filter(Boolean).join(', ');
  const phone = data.phone || '';
  const agentPhone = data.soaAgentPhone || '(503) 324-4511';

  const appointmentDate = data.soaAppointmentDate || today;
  const signedSameDay = data.soaSignatureDate
    ? new Date(data.soaSignatureDate).toLocaleDateString('en-US') === appointmentDate
    : false;

  function addRepresentedPlan(plan) {
    if (!plan || representedPlans.includes(plan)) return;
    const next = [...representedPlans, plan];
    onChange('soaPlansRepresented', next.join(', '));
  }

  function removeRepresentedPlan(plan) {
    const next = representedPlans.filter(item => item !== plan);
    onChange('soaPlansRepresented', next.join(', '));
  }

  return (
    <div>
      <p style={{ fontSize: 14, color: '#4a6288', marginTop: 0, marginBottom: 20, lineHeight: 1.5 }}>
        Complete the agent section of the Scope of Appointment. Beneficiary information is pre-filled from the client's submission.
      </p>

      {/* Beneficiary info — read-only */}
      <div style={{
        background: '#f4f8fc', border: '1.5px solid #c5d5e8',
        borderRadius: 10, padding: '16px 18px', marginBottom: 24,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a3364', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
          Beneficiary Information
        </div>
        <div style={row2}>
          <div>
            <label style={lbl}>Beneficiary Name</label>
            <input style={{ ...inp, background: '#e8f0f8', color: '#4a6288', cursor: 'not-allowed' }} value={fullName} readOnly />
          </div>
          <div>
            <label style={lbl}>Beneficiary Phone</label>
            <input style={{ ...inp, background: '#e8f0f8', color: '#4a6288', cursor: 'not-allowed' }} value={phone} readOnly />
          </div>
        </div>
        <div>
          <label style={lbl}>Beneficiary Address</label>
          <input style={{ ...inp, background: '#e8f0f8', color: '#4a6288', cursor: 'not-allowed' }} value={fullAddress} readOnly />
        </div>
        {selectedProductLabels.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Products Selected by Beneficiary</label>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {selectedProductLabels.map((p, i) => (
                <li key={i} style={{ fontSize: 13, color: '#4a6288', marginBottom: 4 }}>{p}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Agent fields */}
      <div style={row2}>
        <div>
          <label style={lbl}>Agent Name</label>
          <input style={inp} value={data.soaAgentName || ''} onChange={e => onChange('soaAgentName', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Agent Phone</label>
          <input
            style={inp} type="tel" placeholder="(555) 000-0000"
            value={agentPhone}
            onChange={e => onChange('soaAgentPhone', formatPhone(e.target.value))}
          />
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Initial Method of Contact</label>
        <select style={inp} value={data.soaContactMethod || ''} onChange={e => onChange('soaContactMethod', e.target.value)}>
          <option value="">Select…</option>
          {CONTACT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Plan(s) Represented During This Meeting</label>
        <select
          style={inp}
          value=""
          onChange={e => addRepresentedPlan(e.target.value)}
          disabled={selectedProducts.length === 0}
        >
          <option value="">
            {selectedProducts.length ? 'Select a product type to add...' : 'No beneficiary products selected'}
          </option>
          {selectedProducts
            .filter(product => !representedPlans.includes(product.label))
            .map(product => (
              <option key={product.id} value={product.label}>{product.label}</option>
            ))}
        </select>
        {representedPlans.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {representedPlans.map(plan => (
              <button
                key={plan}
                type="button"
                onClick={() => removeRepresentedPlan(plan)}
                style={{
                  border: '1.5px solid #1a3364',
                  borderRadius: 999,
                  background: '#edf5ff',
                  color: '#1a3364',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '7px 11px',
                }}
              >
                {plan} x
              </button>
            ))}
          </div>
        )}
        {representedPlans.length === 0 && selectedProducts.length > 0 && (
          <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 8 }}>
            Choose each product type the agent represented during the meeting.
          </div>
        )}
        </div>

      <div style={{ marginBottom: 24 }}>
        <label style={lbl}>Date Appointment Completed</label>
        <input
          style={inp} type="date"
          value={data.soaAppointmentDate || new Date().toISOString().split('T')[0]}
          onChange={e => onChange('soaAppointmentDate', e.target.value)}
        />
      </div>

      {signedSameDay && (
        <div style={{ marginBottom: 24 }}>
          <label style={lbl}>
            Explanation — SOA Not Documented Prior to Meeting
          </label>
          <p style={{ fontSize: 12, color: '#7a99b8', margin: '0 0 8px' }}>
            Required when the beneficiary signed at the time of the appointment rather than in advance.
          </p>
          <textarea
            style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }}
            rows={4}
            value={data.soaLateExplanation || ''}
            onChange={e => onChange('soaLateExplanation', e.target.value)}
            placeholder="Explain why the SOA was not documented prior to the meeting…"
          />
        </div>
      )}

      {/* Agent signature */}
      <div style={{
        background: '#fff8e8', border: '2px solid #d89b2b',
        borderRadius: 10, padding: '18px 20px',
        boxShadow: '0 8px 24px rgba(26, 51, 100, 0.08)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1a3364', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
          Electronic Signature Agreement
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', userSelect: 'none', marginBottom: 18 }}>
          <input
            type="checkbox"
            checked={!!data.soaAgentAgreedToElectronicSignature}
            onChange={e => {
              onChange('soaAgentAgreedToElectronicSignature', e.target.checked);
              onChange('soaAgentConsentTimestamp', e.target.checked ? new Date().toISOString() : null);
            }}
            style={{ width: 22, height: 22, cursor: 'pointer', accentColor: '#1a3364', marginTop: 0, flex: '0 0 auto' }}
          />
          <span style={{ fontSize: 15, color: '#1a3364', fontWeight: 800, lineHeight: 1.45 }}>
            I agree to use an electronic signature to complete this Scope of Appointment.
          </span>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <div>
            <label style={lbl}>Agent Signature (Type Full Name)</label>
            <input
              style={{ ...inp, fontStyle: 'italic', fontSize: 16, letterSpacing: '0.02em' }}
              value={data.soaAgentSignature || ''}
              onChange={e => onChange('soaAgentSignature', e.target.value)}
              placeholder="Full legal name"
              disabled={!data.soaAgentAgreedToElectronicSignature}
            />
          </div>
          <div>
            <label style={lbl}>Signature Date</label>
            <input
              style={{ ...inp, background: '#e8f0f8', color: '#4a6288', cursor: 'not-allowed' }}
              value={today}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
}
